// pages/grant-detail.ts — applicant's per-grant view.
//
// Mirrors frontend2/User.jsx :: GrantDetailPage. Tabs:
//   • Overview  — header card + key-value table + description
//   • Documents — list w/ Preview (PDF only) / Download / Open-link
//   • Messages  — thread + new-message box (api.postMessage)
//   • Activity  — status-history timeline
//
// PDF preview uses api.getDocumentBlob (hoisted from User.jsx) and
// opens inside a Bootstrap modal. The Blob URL is revoked when the
// modal closes (hidden.bs.modal listener) so we don't leak memory.

import type { Page, PageContext } from '../router';
import {
  html, esc, fmtMoney, fmtDate, fmtDateTime, fmtFileSize, statusPillClass, statusColor,
} from '../util';

interface Grant {
  GrantId: number;
  Status: string;
  ProjectName: string;
  OrganizationLegalName?: string;
  ReferenceNumber?: string;
  TaxExemptStatus?: string;
  RequestedAmount?: number | null;
  SubmittedAt?: string;
  UpdatedAt?: string;
  ProjectStartDate?: string;
  ProjectEndDate?: string;
  ProjectDescription?: string;
}

interface DocRow {
  DocumentId: number;
  DocumentType: string;
  FileName: string;
  FileSize: number;
  FileExtension?: string;
  FilePath?: string;
  UploadedAt?: string;
}

interface Message {
  MessageId?: number;
  SenderName: string;
  SenderRole: 'User' | 'Approver' | 'Admin' | string;
  SentAt?: string;
  MessageText: string;
}

interface HistoryRow {
  OldStatus: string | null;
  NewStatus: string;
  ChangedAt?: string;
  ChangedByName?: string;
  Notes?: string;
}

type TabId = 'overview' | 'documents' | 'messages' | 'activity';
const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'overview',  label: 'Overview' },
  { id: 'documents', label: 'Documents' },
  { id: 'messages',  label: 'Messages' },
  { id: 'activity',  label: 'Activity' },
];

// ─── Tiny inline helpers (kept inline so grant-detail is self-contained) ─

function roleBadge(role: string | undefined | null): string {
  const r = role || 'User';
  let bg = 'var(--paper-2)';
  let fg = 'var(--ink-soft)';
  if (r === 'Admin')    { bg = 'var(--accent-soft)';  fg = 'var(--accent)'; }
  if (r === 'Approver') { bg = 'var(--primary-soft)'; fg = 'var(--st-approved)'; }
  return html`<span style="padding: 2px 8px; border-radius: 999px;
    font-size: 11.5px; font-weight: 600; letter-spacing: 0.04em;
    text-transform: uppercase; background: ${{ __raw: bg }}; color: ${{ __raw: fg }};">${r}</span>`;
}

function renderTimeline(events: Array<{ title: string; date?: string; desc?: string; color?: string }>): string {
  if (!events || events.length === 0) {
    return html`<p class="t-body" style="margin: 0;">No activity recorded yet.</p>`;
  }
  const rows = events.map((ev, i) => {
    const last = i === events.length - 1;
    return `
      <li style="display: grid; grid-template-columns: 12px 1fr; gap: 14px;
                 padding: 10px 0; border-bottom: ${last ? 'none' : '1px solid var(--rule)'};">
        <span style="width: 10px; height: 10px; border-radius: 999px;
                     background: ${ev.color || 'var(--primary)'}; margin-top: 7px;"></span>
        <div>
          <div style="font-size: 14px; color: var(--ink);">${esc(ev.title)}</div>
          ${ev.date ? `<div class="t-small" style="font-size: 12.5px;">${esc(ev.date)}</div>` : ''}
          ${ev.desc ? `<p class="t-body" style="margin: 6px 0 0; font-size: 13.5px;">${esc(ev.desc)}</p>` : ''}
        </div>
      </li>
    `;
  }).join('');
  return html`<ol style="list-style: none; margin: 0; padding: 0;">${{ __raw: rows }}</ol>`;
}

// ─── Per-tab renderers ─────────────────────────────────────────────

function renderOverview(g: Grant): string {
  const rows: Array<[string, string]> = [
    ['Organization',     g.OrganizationLegalName || '\u2014'],
    ['Project name',     g.ProjectName],
    ['Requested amount', fmtMoney(g.RequestedAmount || 0)],
    ['501(c)(3) status', g.TaxExemptStatus === 'Yes' ? 'Yes \u2014 verified' : (g.TaxExemptStatus || '\u2014')],
    ['Project timeline',
     g.ProjectStartDate && g.ProjectEndDate
       ? `${fmtDate(g.ProjectStartDate)} \u2013 ${fmtDate(g.ProjectEndDate)}`
       : '\u2014'],
  ];
  const rowsHtml = rows.map(([k, v]) => `
    <div class="d-flex" style="gap: 16px; margin-bottom: 12px; font-size: 14px;
                               border-bottom: 1px solid var(--rule); padding-bottom: 12px;">
      <span class="t-small" style="min-width: 160px;">${esc(k)}</span>
      <span>${esc(v)}</span>
    </div>
  `).join('');

  return html`
    <div class="card" style="padding: 24px;">
      <h3 class="t-h4 serif" style="margin: 0 0 20px; font-size: 20px;">Grant overview</h3>
      ${{ __raw: rowsHtml }}
      ${{ __raw: g.ProjectDescription ? html`
        <div style="margin-top: 12px;">
          <p class="label" style="margin-bottom: 8px;">Project description</p>
          <p class="t-body" style="font-size: 14px; line-height: 1.75; white-space: pre-wrap;">${g.ProjectDescription}</p>
        </div>
      ` : '' }}
    </div>
  `;
}

function renderDocumentsTab(docs: DocRow[]): string {
  if (!docs || docs.length === 0) {
    return html`
      <div class="card" style="padding: 24px;">
        <h3 class="t-h4 serif" style="margin: 0 0 20px; font-size: 20px;">Uploaded documents</h3>
        <p class="t-body" style="margin: 0;">No documents uploaded.</p>
      </div>
    `;
  }

  const rows = docs.map(doc => {
    const isLink = /^https?:\/\//i.test(doc.FilePath || '');
    const ext = (doc.FileExtension || '').replace('.', '').toLowerCase();
    const isPdf = ext === 'pdf';

    let actions = '';
    if (isLink) {
      actions = `
        <a href="${esc(doc.FilePath || '')}" target="_blank" rel="noopener noreferrer"
           class="btn btn-outline-primary btn-sm">Open link →</a>
      `;
    } else {
      const previewBtn = isPdf
        ? `<button type="button" class="btn btn-text btn-sm"
                   data-action="preview-doc" data-doc-id="${doc.DocumentId}">Preview</button>`
        : '';
      actions = `
        <div class="d-flex" style="gap: 6px;">
          ${previewBtn}
          <button type="button" class="btn btn-outline-primary btn-sm"
                  data-action="download-doc" data-doc-id="${doc.DocumentId}"
                  data-doc-name="${esc(doc.FileName)}">Download</button>
        </div>
      `;
    }

    return `
      <div class="d-flex" style="justify-content: space-between; align-items: center;
                                 padding: 12px 0; border-bottom: 1px solid var(--rule); gap: 16px;">
        <div style="min-width: 0;">
          <div style="font-weight: 500; font-size: 14px; word-break: break-word;">${esc(doc.FileName)}</div>
          <div class="t-small">${esc(doc.DocumentType)} · ${esc(fmtFileSize(doc.FileSize))} · ${esc(fmtDate(doc.UploadedAt))}</div>
        </div>
        ${actions}
      </div>
    `;
  }).join('');

  return html`
    <div class="card" style="padding: 24px;">
      <h3 class="t-h4 serif" style="margin: 0 0 20px; font-size: 20px;">Uploaded documents</h3>
      ${{ __raw: rows }}
    </div>
  `;
}

function renderMessagesTab(messages: Message[], draftText: string, sending: boolean): string {
  const list = messages.length === 0
    ? '<p class="t-body" style="margin: 0;">No messages yet.</p>'
    : messages.map(m => `
        <div style="background: var(--card); border: 1px solid var(--rule);
                    border-radius: 10px; padding: 14px 16px;">
          <div class="d-flex" style="gap: 8px; align-items: center; margin-bottom: 6px; flex-wrap: wrap;">
            <span style="font-weight: 500; font-size: 13.5px;">${esc(m.SenderName)}</span>
            ${roleBadge(m.SenderRole)}
            <span class="t-small">${esc(fmtDateTime(m.SentAt))}</span>
          </div>
          <p class="t-body" style="margin: 0; font-size: 14px; white-space: pre-wrap;">${esc(m.MessageText)}</p>
        </div>
      `).join('');

  const disabledAttr = (!draftText.trim() || sending) ? ' disabled' : '';
  return html`
    <div class="card" style="padding: 24px;">
      <h3 class="t-h4 serif" style="margin: 0 0 20px; font-size: 20px;">Messages</h3>
      <div id="gd-msg-list" style="display: flex; flex-direction: column; gap: 14px; margin-bottom: 24px;">
        ${{ __raw: list }}
      </div>
      <div style="border-top: 1px solid var(--rule); padding-top: 20px;">
        <div class="gf-field" style="margin-bottom: 16px;">
          <textarea id="gd-msg-input" class="form-control" rows="4"
                    placeholder="Type your message here…" style="resize: vertical;">${esc(draftText)}</textarea>
        </div>
        <button type="button" class="btn btn-primary" data-action="send-message"${disabledAttr}>
          ${sending ? 'Sending…' : 'Send message'}
        </button>
      </div>
    </div>
  `;
}

function activityCard(history: HistoryRow[]): string {
  const events = history.map((h, i) => ({
    title: i === 0 && h.OldStatus == null ? 'Application submitted' : `${h.OldStatus} \u2192 ${h.NewStatus}`,
    date:  fmtDateTime(h.ChangedAt),
    desc:  h.Notes || h.ChangedByName || '',
    color: statusColor(h.NewStatus),
  }));
  return html`
    <div class="card" style="padding: 24px;">
      <h3 class="t-h4 serif" style="margin: 0 0 20px; font-size: 20px;">Application activity</h3>
      ${{ __raw: renderTimeline(events) }}
    </div>
  `;
}

// ─── Preview-modal scaffold ────────────────────────────────────────

function renderPreviewModal(): string {
  return html`
    <div class="modal fade" id="gdPreviewModal" tabindex="-1"
         aria-labelledby="gdPreviewModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 900px;">
        <div class="modal-content">
          <div class="modal-header" style="border-bottom: 1px solid var(--rule);">
            <h3 class="t-h4 serif" id="gdPreviewModalLabel" style="margin: 0; font-size: 20px;">
              Document preview
            </h3>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" style="padding: 0;">
            <iframe id="gd-preview-frame" title="Document preview"
                    style="width: 100%; height: 70vh; border: none; display: block; background: var(--paper-2);"></iframe>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ─── Page ──────────────────────────────────────────────────────────

const GrantDetailPage: Page = {
  guard(ctx: PageContext): string | null {
    return ctx.user ? null : 'login';
  },

  render(ctx: PageContext): string {
    const _idRaw = ctx.params.get('id');
    const _idNum = _idRaw == null ? NaN : Number(_idRaw);
    const id: number | null = Number.isFinite(_idNum) ? _idNum : null;
    if (id == null) {
      return html`
        <main class="wrap" style="max-width: 600px; padding: 60px 24px; text-align: center;">
          <div class="card" style="padding: 24px; background: var(--accent-soft); border-color: var(--accent);">
            <p class="t-body" style="margin: 0 0 16px;">No grant selected.</p>
            <a class="btn btn-primary" data-nav="user-dashboard">Back to dashboard</a>
          </div>
        </main>
      `;
    }

    return html`
      <nav class="wrap" style="padding: 20px 40px 0; max-width: 1240px;">
        <div class="t-small" style="color: var(--ink-soft);">
          <a class="gf-breadcrumb" data-nav="user-dashboard">Dashboard</a>
          <span style="margin: 0 8px; opacity: 0.5;">/</span>
          <span style="color: var(--ink-2);">Grant details</span>
        </div>
      </nav>

      <main class="wrap" style="max-width: 1000px; padding: 24px 24px 96px;">
        <div id="gd-root">
          <p class="t-body" style="padding: 60px 0; text-align: center;">Loading grant…</p>
        </div>
        ${{ __raw: renderPreviewModal() }}
      </main>
    `;
  },

  mount(root: HTMLElement, ctx: PageContext): void {
    const _idRaw = ctx.params.get('id');
    const _idNum = _idRaw == null ? NaN : Number(_idRaw);
    const id: number | null = Number.isFinite(_idNum) ? _idNum : null;
    if (id == null) return;

    let alive = true;
    let activeTab: TabId = 'overview';
    let msgDraft = '';
    let sending  = false;
    let previewUrl: string | null = null;
    let grant: Grant | null = null;
    let documents: DocRow[] = [];
    let messages:  Message[] = [];
    let history:   HistoryRow[] = [];

    const rootEl    = root.querySelector('#gd-root')         as HTMLElement | null;
    const modalEl   = root.querySelector('#gdPreviewModal')  as HTMLElement | null;
    const frameEl   = root.querySelector('#gd-preview-frame') as HTMLIFrameElement | null;

    // Bootstrap modal helper.
    function getModalInstance(): { show(): void; hide(): void } | null {
      if (!modalEl) return null;
      const Bs = (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance(el: HTMLElement): { show(): void; hide(): void } } } }).bootstrap;
      return Bs ? Bs.Modal.getOrCreateInstance(modalEl) : null;
    }

    // Revoke blob URL after the modal finishes hiding so the iframe
    // can drop its reference cleanly.
    if (modalEl) {
      modalEl.addEventListener('hidden.bs.modal', () => {
        if (frameEl) frameEl.src = 'about:blank';
        if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null; }
      });
    }

    // ── Renderers ─────────────────────────────────────────────
    function renderHeaderCard(g: Grant): string {
      const meta: Array<[string, string]> = [
        ['Date submitted',   fmtDate(g.SubmittedAt)],
        ['Requested amount', fmtMoney(g.RequestedAmount || 0)],
        ['Last updated',     fmtDate(g.UpdatedAt)],
      ];
      const cells = meta.map(([k, v]) => `
        <div>
          <p class="t-small">${esc(k)}</p>
          <p style="font-weight: 500; font-size: 15px; margin: 0;">${esc(v)}</p>
        </div>
      `).join('');

      return html`
        <div class="card" style="margin-bottom: 24px; padding: 24px 28px;">
          <div class="d-flex" style="justify-content: space-between; align-items: flex-start;
                                     flex-wrap: wrap; gap: 16px; margin-bottom: 16px;">
            <div>
              <h1 class="t-h2 serif" style="margin: 0; font-size: 30px;">${g.ProjectName}</h1>
              ${{ __raw: g.ReferenceNumber ? html`
                <p class="t-small" style="margin-top: 4px;">
                  Reference: <strong style="color: var(--ink);">#${g.ReferenceNumber}</strong>
                </p>
              ` : '' }}
            </div>
            <span class="pill ${statusPillClass(g.Status)}">${g.Status}</span>
          </div>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
                      gap: 16px; border-top: 1px solid var(--rule); padding-top: 16px;">
            ${{ __raw: cells }}
          </div>
        </div>
      `;
    }

    function renderTabBar(): string {
      const tabs = TABS.map(t => {
        const isActive = activeTab === t.id;
        const badge = t.id === 'messages' && messages.length > 0
          ? `<span style="margin-left: 6px; background: var(--st-denied); color: var(--paper);
                          border-radius: 99px; padding: 1px 6px; font-size: 11px; font-weight: 600;">${messages.length}</span>`
          : '';
        return `
          <button type="button" data-action="set-tab" data-tab="${t.id}"
                  style="padding: 10px 18px; font-size: 14px; font-weight: 500; border: none;
                         cursor: pointer; background: transparent;
                         color: ${isActive ? 'var(--primary)' : 'var(--ink-soft)'};
                         border-bottom: 2px solid ${isActive ? 'var(--primary)' : 'transparent'};
                         margin-bottom: -1px;">
            ${esc(t.label)}${badge}
          </button>
        `;
      }).join('');
      return html`
        <div class="d-flex" style="gap: 8px; border-bottom: 1px solid var(--rule);
                                   margin-bottom: 16px; flex-wrap: wrap;">
          ${{ __raw: tabs }}
        </div>
      `;
    }

    function renderTabBody(): string {
      if (!grant) return '';
      if (activeTab === 'overview')  return renderOverview(grant);
      if (activeTab === 'documents') return renderDocumentsTab(documents);
      if (activeTab === 'messages')  return renderMessagesTab(messages, msgDraft, sending);
      if (activeTab === 'activity')  return activityCard(history);
      return '';
    }

    function repaint(): void {
      if (!rootEl) return;
      if (!grant) {
        rootEl.innerHTML = `
          <div class="card" style="padding: 24px; background: var(--accent-soft); border-color: var(--accent);">
            <p class="t-body" style="margin: 0 0 16px;">Grant not found.</p>
            <a class="btn btn-primary" data-nav="user-dashboard">Back to dashboard</a>
          </div>
        `;
        return;
      }
      rootEl.innerHTML = renderHeaderCard(grant) + renderTabBar() + renderTabBody();
    }

    // ── Actions ────────────────────────────────────────────────
    function setTab(t: TabId): void {
      activeTab = t;
      repaint();
    }

    async function previewDoc(docId: number): Promise<void> {
      if (id == null) return;
      try {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        previewUrl = await window.api.getDocumentBlob(id, docId);
        if (!alive) return;
        if (frameEl) frameEl.src = previewUrl;
        getModalInstance()?.show();
      } catch (err) {
        alert((err as Error).message || 'Could not load preview.');
      }
    }

    async function downloadDoc(docId: number, fileName: string): Promise<void> {
      if (id == null) return;
      try {
        await window.api.downloadDocument(id, docId, fileName);
      } catch (err) {
        alert((err as Error).message || 'Download failed.');
      }
    }

    async function sendMessage(): Promise<void> {
      if (id == null) return;
      const textarea = rootEl?.querySelector<HTMLTextAreaElement>('#gd-msg-input');
      const text = (textarea?.value || '').trim();
      if (!text) return;
      sending = true;
      msgDraft = text;
      repaint();
      try {
        await window.api.postMessage(id, text);
        if (!alive) return;
        const msgs = await window.api.listMessages(id) as Message[];
        if (!alive) return;
        messages = msgs || [];
        msgDraft = '';
      } catch (err) {
        alert((err as Error).message || 'Could not send message.');
      } finally {
        sending = false;
        if (alive) repaint();
      }
    }

    // ── Event delegation ───────────────────────────────────────
    root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const actionEl = t.closest('[data-action]') as HTMLElement | null;
      if (!actionEl) return;
      const action = actionEl.getAttribute('data-action');
      switch (action) {
        case 'set-tab': {
          const tab = actionEl.getAttribute('data-tab') as TabId | null;
          if (tab) setTab(tab);
          break;
        }
        case 'preview-doc': {
          const docId = Number(actionEl.getAttribute('data-doc-id'));
          if (docId) void previewDoc(docId);
          break;
        }
        case 'download-doc': {
          const docId = Number(actionEl.getAttribute('data-doc-id'));
          const name  = actionEl.getAttribute('data-doc-name') || '';
          if (docId) void downloadDoc(docId, name);
          break;
        }
        case 'send-message':
          void sendMessage();
          break;
      }
    });

    // Keep the textarea value sticky between repaints + toggle Send-button state.
    root.addEventListener('input', (e) => {
      const ta = (e.target as HTMLElement | null)?.closest('#gd-msg-input') as HTMLTextAreaElement | null;
      if (!ta) return;
      msgDraft = ta.value;
      const btn = rootEl?.querySelector<HTMLButtonElement>('[data-action="send-message"]');
      if (btn) btn.disabled = !msgDraft.trim() || sending;
    });

    // ── Fetch ──────────────────────────────────────────────────
    Promise.all([
      window.api.getGrant(id),
      window.api.listDocuments(id),
      window.api.listMessages(id),
      window.api.getHistory(id),
    ]).then(([g, docs, msgs, hist]) => {
      if (!alive) return;
      grant     = g as Grant;
      documents = (docs as DocRow[]) || [];
      messages  = (msgs as Message[]) || [];
      history   = (hist as HistoryRow[]) || [];
      repaint();
    }).catch(err => {
      if (!alive) return;
      if (rootEl) {
        rootEl.innerHTML = `
          <div class="card" style="padding: 24px; background: var(--accent-soft); border-color: var(--accent);">
            <p class="t-body" style="margin: 0 0 16px;">Could not load grant (${esc((err as Error).message || 'network')}).</p>
            <a class="btn btn-primary" data-nav="user-dashboard">Back to dashboard</a>
          </div>
        `;
      }
    });

    (GrantDetailPage as Page & { _alive?: () => void })._alive = () => {
      alive = false;
      if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null; }
    };
  },

  unmount(): void {
    const stop = (GrantDetailPage as Page & { _alive?: () => void })._alive;
    if (stop) stop();
  },
};

export default GrantDetailPage;
